import type { EntityParamKey } from './buildTestLink';

/** What every picker shows. `siteId` is null for entities no site owns (landing pages, offers). */
export interface PickerOption {
  id: number;
  label: string;
  siteId: number | null;
  inactive: boolean;
  /** Landing page groups only: the landing pages configured in any of the group's segments. */
  landingPageIds?: number[];
}

export interface DomainOption extends PickerOption {
  name: string;
}

export interface PickerList<T extends PickerOption = PickerOption> {
  options: T[];
  loading: boolean;
  error: boolean;
}

/** LPGs and sequences belong to a site; landing pages and offers reach one only indirectly. */
export const ENTITY_SITE_SCOPED: Record<EntityParamKey, boolean> = {
  lpid: false,
  lpgid: true,
  sequence_id: true,
  offer_id: false,
};

// The id is in the label so typing an id into the picker finds it, same as a name.
export const optionLabel = (id: number, name: string | null | undefined, inactive = false): string =>
  `#${id} · ${name || '(unnamed)'}${inactive ? ' (inactive)' : ''}`;

const newestFirst = <T extends { id: number }>(list: T[]): T[] => [...list].sort((a, b) => b.id - a.id);

type SiteRow = { id: number; domain?: string | null };
type DomainRow = { id: number; name: string | null; isActive?: boolean | null; site?: { id: number } | null };
type LandingPageRow = {
  id: number;
  layout?: { name?: string } | null;
  offer?: { name?: string; isActive?: boolean } | null;
};
type LpgRow = {
  id: number;
  name?: string | null;
  isActive?: boolean | null;
  site?: { id: number } | null;
  landingPagesGroupSegments?:
    | {
        landingPagesLandingPageGroupSegments?: { landingPage?: { id?: number } | null }[] | null;
      }[]
    | null;
};

/** Every landing page a group can serve, across its segments, without repeats. */
export const groupLandingPageIds = (row: LpgRow): number[] => [
  ...new Set(
    (row.landingPagesGroupSegments ?? []).flatMap((segment) =>
      (segment?.landingPagesLandingPageGroupSegments ?? [])
        .map((entry) => entry?.landingPage?.id)
        .filter((id): id is number => id != null)
    )
  ),
];
type SequenceRow = { id: number; name?: string | null; siteId?: number | null; isActive?: boolean | null };
type OfferRow = { id: number; name?: string | null; isActive?: boolean | null };

export const toSiteOptions = (rows: SiteRow[]): PickerOption[] =>
  [...rows]
    .sort((a, b) => a.id - b.id)
    .map((row) => ({ id: row.id, label: optionLabel(row.id, row.domain), siteId: row.id, inactive: false }));

export const toDomainOptions = (rows: DomainRow[]): DomainOption[] =>
  rows
    .map((row) => {
      const inactive = row.isActive === false;
      const name = row.name ?? '';
      return {
        id: row.id,
        name,
        label: name ? `${name}${inactive ? ' (inactive)' : ''}` : optionLabel(row.id, null, inactive),
        siteId: row.site?.id ?? null,
        inactive,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

export const toLandingPageOptions = (rows: LandingPageRow[]): PickerOption[] =>
  newestFirst(rows).map((row) => {
    const inactive = row.offer?.isActive === false;
    const name = [row.offer?.name, row.layout?.name].filter(Boolean).join(' — ');
    return { id: row.id, label: optionLabel(row.id, name, inactive), siteId: null, inactive };
  });

export const toLpgOptions = (rows: LpgRow[]): PickerOption[] =>
  newestFirst(rows).map((row) => {
    const inactive = row.isActive === false;
    return {
      id: row.id,
      label: optionLabel(row.id, row.name, inactive),
      siteId: row.site?.id ?? null,
      inactive,
      // Absent when the row carried no segments at all, so an unreadable group never empties the list.
      landingPageIds: Array.isArray(row.landingPagesGroupSegments) ? groupLandingPageIds(row) : undefined,
    };
  });

export const toSequenceOptions = (rows: SequenceRow[]): PickerOption[] =>
  newestFirst(rows).map((row) => {
    const inactive = row.isActive === false;
    return { id: row.id, label: optionLabel(row.id, row.name, inactive), siteId: row.siteId ?? null, inactive };
  });

export const toOfferOptions = (rows: OfferRow[]): PickerOption[] =>
  newestFirst(rows).map((row) => {
    const inactive = row.isActive === false;
    return { id: row.id, label: optionLabel(row.id, row.name, inactive), siteId: null, inactive };
  });

/**
 * Narrow to one site. The current selection always stays listed — the user may pick a domain and
 * then point it at another site (test site 75), and the picker must keep showing what is chosen.
 */
export function forSite<T extends PickerOption>(
  options: T[],
  siteId: number | null,
  selectedId: number | null,
  showAll = false
): T[] {
  if (siteId == null || showAll) return options;
  return options.filter((option) => option.siteId === siteId || option.id === selectedId);
}

export const siteMismatch = (option: PickerOption | undefined, siteId: number | null): boolean =>
  !!option && option.siteId != null && siteId != null && option.siteId !== siteId;

/**
 * Narrow landing pages to the ones a group serves. As with sites, the current selection stays
 * listed, and a group whose segments could not be read (no list at all) narrows nothing.
 */
export function forGroup<T extends PickerOption>(
  options: T[],
  group: PickerOption | undefined,
  selectedId: number | null,
  showAll = false
): T[] {
  if (!group?.landingPageIds || showAll) return options;
  const ids = new Set(group.landingPageIds);
  return options.filter((option) => ids.has(option.id) || option.id === selectedId);
}

/**
 * A landing page has no site of its own, but the groups that serve it do. When every group serving
 * it belongs to one site, that is its site; otherwise (no group, or groups on several sites) none.
 */
export function siteOfLandingPage(landingPageId: number | null | undefined, groups: PickerOption[]): number | null {
  if (landingPageId == null) return null;
  const sites = new Set(
    groups
      .filter((group) => group.siteId != null && group.landingPageIds?.includes(landingPageId))
      .map((group) => group.siteId)
  );
  return sites.size === 1 ? ([...sites][0] as number) : null;
}
